import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { useLang, UI } from "@/lib/lang";

function LangToggle({ mobile, lang, setLang }) {
  return (
    <div
      className={`flex items-center ${mobile ? "" : "border border-white/15 divide-x divide-white/15"}`}
      data-testid="lang-toggle"
    >
      <button
        onClick={() => setLang("en")}
        className={`num-tick px-2 py-1 transition-colors ${
          lang === "en" ? "text-[#1E7FFF]" : "text-white/50 hover:text-white"
        }`}
        aria-label="English"
        data-testid="lang-en"
      >
        EN
      </button>
      <button
        onClick={() => setLang("es")}
        className={`num-tick px-2 py-1 transition-colors ${
          lang === "es" ? "text-[#1E7FFF]" : "text-white/50 hover:text-white"
        }`}
        aria-label="Español"
        data-testid="lang-es"
      >
        ES
      </button>
    </div>
  );
}

export default function Nav({ brand }) {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#services", label: t(UI.navServices), id: "services" },
    { href: "#manifesto", label: t(UI.navMethod), id: "manifesto" },
    { href: "#work", label: t(UI.navWork), id: "work" },
    { href: "#testimonials", label: t(UI.navReviews), id: "testimonials" },
    { href: "#contact", label: t(UI.navContact), id: "contact" },
  ];

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/10 py-3" : "py-5"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 md:px-14 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-brand">
          <img
            src={brand?.logo}
            alt={brand?.name}
            className={`transition-all duration-500 ${scrolled ? "h-8" : "h-10"} w-auto`}
          />
          <span className="hidden sm:block num-tick text-white/70">
            {brand?.name || "El Cuquito"}
          </span>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="font-mono-x text-[12px] tracking-[0.18em] uppercase text-white/85 hover:text-[#1E7FFF] transition-colors"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </a>
          ))}
          <LangToggle lang={lang} setLang={setLang} />
          <a
            href={`https://wa.me/1${(brand?.whatsapp || "").replace(/\D/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary py-3 px-4 text-[11px]"
            data-testid="nav-cta-wa"
          >
            <MessageCircle size={12} />
            {t(UI.wa)}
          </a>
        </div>

        <div className="md:hidden flex items-center gap-4">
          <LangToggle mobile lang={lang} setLang={setLang} />
          <button
            onClick={() => setOpen(!open)}
            aria-label="menu"
            className="w-10 h-10 flex flex-col justify-center gap-1.5 items-center"
            data-testid="nav-menu-toggle"
          >
            <span className={`block w-6 h-px bg-white transition-transform ${open ? "translate-y-[3px] rotate-45" : ""}`} />
            <span className={`block w-6 h-px bg-white transition-transform ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden bg-black border-t border-white/10 overflow-hidden"
          >
            <div className="px-8 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-display uppercase text-2xl text-white"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={`https://wa.me/1${(brand?.whatsapp || "").replace(/\D/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary mt-3 self-start"
              >
                <MessageCircle size={12} /> {t(UI.wa)}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
